import React, { useState, useEffect, useCallback } from 'react';
import { Button, Tooltip, TablePagination } from '@mui/material';
import { Power, Pencil, PowerOff, Plus } from 'lucide-react';
import moment from 'moment';

import Modal from '../../../components/modal/Index';
import SnackBar from '../../../components/snackbar/snackbar';
import Page from '../../../components/Page';
import adminServices from '../../../services/adminServices';
import AddAdminUserForm from './AddAdminUserForm';
import ResponsiveTable from '../../../components/Table/Responsivetable';

export default function AdminUserList() {
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('create');
  const [editData, setEditData] = useState(null);
  const [userId, setUserId] = useState(null);

  const [statusAlert, setStatusAlert] = useState(false);
  const [statusTarget, setStatusTarget] = useState({ id: null, name: '', action: 'Deactivate' });

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarTitle, setSnackbarTitle] = useState('');

  const getUsers = useCallback(async (currentPage, limit, searchTerm, role = '') => {
    setLoading(true);
    try {
      const response = await adminServices.getAdminUsers(currentPage + 1, limit, searchTerm, role);
      if (response.ok) {
        setUsers(response.data?.data || []);
        setTotalCount(response.data?.pagination?.total || 0);
      }
    } catch (error) {
      console.log(error, 'failed to [getAdminUsers]');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getUsers(page, rowsPerPage, search, roleFilter);
  }, [page, rowsPerPage, search, roleFilter, getUsers]);

  useEffect(() => {
    adminServices.getAssignableRoles()
      .then((res) => { if (res.ok) setRoles(res.data?.data || []); })
      .catch((error) => console.log(error, 'failed to [getAssignableRoles]'));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(0);
      setSearch(searchInput);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const snakbarHandle = (message) => {
    setOpenSnackbar(true);
    setSnackbarTitle(message);
  };

  const handleStatusChange = async () => {
    const status = statusTarget.action === 'Deactivate' ? 'INACTIVE' : 'ACTIVE';
    try {
      const response = await adminServices.setAdminUserStatus(statusTarget.id, status);
      setSnackbarTitle(response.ok
        ? (response.data?.message || `${statusTarget.action}d successfully`)
        : (response.data?.message || 'Something went wrong'));
      if (response.ok) getUsers(page, rowsPerPage, search, roleFilter);
    } catch (error) {
      console.log(error, 'failed to [setAdminUserStatus]');
      setSnackbarTitle('Something went wrong');
    } finally {
      setStatusAlert(false);
      setOpenSnackbar(true);
    }
  };

  const openStatusModal = (row, action) => {
    setStatusTarget({
      id: row.id,
      name: `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email,
      action,
    });
    setStatusAlert(true);
  };

  function StatusModal() {
    if (!statusAlert) return null;
    const isDeactivate = statusTarget.action === 'Deactivate';
    return (
      <div className="fixed inset-0 bg-black/35 flex items-center justify-center z-[1300]">
        <div className="bg-white rounded-xl px-7 pt-8 pb-6 w-[380px] max-w-[90vw] shadow-2xl">
          <h2 className="m-0 mb-2.5 text-lg font-bold text-gray-900">
            {isDeactivate ? 'Deactivate User' : 'Reactivate User'}
          </h2>
          <p className="m-0 mb-7 text-sm text-gray-600 leading-relaxed">
            <strong>{statusTarget.name}</strong>
            {isDeactivate ? ' will lose access to the platform.' : ' will regain access to the platform.'}
          </p>
          <div className="flex justify-end gap-2.5">
            <button
              type="button"
              onClick={() => setStatusAlert(false)}
              className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleStatusChange}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold transition-all ${
                isDeactivate ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {statusTarget.action}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const columns = [
    {
      key: 'name',
      label: 'NAME',
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => (
        <span className="font-semibold text-gray-900 text-sm">
          {`${row.first_name || ''} ${row.last_name || ''}`.trim() || '—'}
        </span>
      ),
    },
    {
      key: 'email',
      label: 'EMAIL',
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => (
        <Tooltip title={row.email}>
          <span className="text-sm text-gray-600 truncate max-w-[220px] block">{row.email}</span>
        </Tooltip>
      ),
    },
    {
      key: 'role',
      label: 'ROLE',
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-indigo-50 text-indigo-700 border-indigo-200">
          {row.role_name || row.role_code || '—'}
        </span>
      ),
    },
    {
      key: 'phone',
      label: 'PHONE',
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => <span className="text-sm text-gray-600">{row.phone || '—'}</span>,
    },
    {
      key: 'created',
      label: 'CREATED',
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => (
        <span className="text-sm text-gray-600">
          {row.create_datetime ? moment(row.create_datetime).format('MMM DD, YYYY') : '—'}
        </span>
      ),
    },
    {
      key: 'lastLogin',
      label: 'LAST LOGIN',
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => (
        <span className="text-sm text-gray-600">
          {row.last_login_datetime ? moment(row.last_login_datetime).format('MMM DD, YYYY') : 'Never'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'STATUS',
      centered: true,
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => {
        const isActive = row.status_code === 'ACTIVE';
        return (
          <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold border ${
            isActive ? 'text-green-700 bg-green-50 border-green-200' : 'text-[#71717A] bg-[#71717A]/10 border-[#71717A]/30'
          }`}>
            {isActive ? 'Active' : 'Inactive'}
          </span>
        );
      },
    },
    {
      key: 'actions',
      label: 'ACTIONS',
      centered: true,
      headerClassName: 'uppercase tracking-widest text-xs text-gray-500',
      render: (row) => {
        const isActive = row.status_code === 'ACTIVE';
        return (
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => { setMode('edit'); setUserId(row.id); setEditData(row); setModalOpen(true); }}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors bg-transparent border-0 cursor-pointer"
            >
              <Pencil size={16} />
              <span>Edit</span>
            </button>
            {isActive ? (
              <button
                type="button"
                onClick={() => openStatusModal(row, 'Deactivate')}
                className="flex items-center gap-1 text-red-500 hover:text-red-700 text-sm font-medium transition-colors bg-transparent border-0 cursor-pointer"
              >
                <PowerOff size={16} />
                <span>Deactivate</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => openStatusModal(row, 'Reactivate')}
                className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm font-medium transition-colors bg-transparent border-0 cursor-pointer"
              >
                <Power size={16} />
                <span>Reactivate</span>
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Page title="Admin Users">
      <div className="flex items-start justify-between m-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Users</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Staff accounts that use the admin layout. Page access is managed in Roles &amp; Permissions.
          </p>
        </div>
        <Button
          variant="contained"
          onClick={() => { setMode('create'); setEditData(null); setUserId(null); setModalOpen(true); }}
          style={{
            backgroundColor: '#2563EB', borderRadius: '8px', textTransform: 'none',
            fontWeight: 600, fontSize: '0.875rem', padding: '8px 18px', boxShadow: 'none',
          }}
        >
          <Plus size={16} style={{ marginRight: 6 }} />
          Add User
        </Button>
      </div>

      <div className="mx-6 mb-4 flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Search by name or email..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full max-w-sm px-4 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
        />
        <select
          value={roleFilter}
          onChange={(e) => { setPage(0); setRoleFilter(e.target.value); }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-100 cursor-pointer"
        >
          <option value="">All roles</option>
          {roles.map((r) => (
            <option key={r.id} value={r.code}>{r.description || r.code}</option>
          ))}
        </select>
      </div>

      <div className="mx-6 bg-white rounded-xl border border-gray-200 overflow-hidden">
        <ResponsiveTable
          columns={columns}
          data={users}
          loading={loading}
          emptyStateComponent={
            <div className="text-center py-16 text-gray-400 text-sm">No admin users found.</div>
          }
        />
      </div>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      <Modal
        title={mode === 'create' ? 'Add Admin User' : 'Edit Admin User'}
        open={modalOpen}
        toggle={() => setModalOpen(false)}
      >
        <AddAdminUserForm
          mode={mode}
          data={editData}
          userId={userId}
          getList={() => getUsers(page, rowsPerPage, search, roleFilter)}
          toggle={() => setModalOpen(false)}
          snackBarControl={snakbarHandle}
        />
      </Modal>

      <StatusModal />

      <SnackBar open={openSnackbar} snackbarTitle={snackbarTitle} close={() => setOpenSnackbar(false)} />
    </Page>
  );
}
